import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, CircularProgress, Skeleton, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import logo from '../assets/logo1.png';

// ── Paleta institucional Capellanías (spec sección 6.1) ─────────────────────
const C = {
  navy:    '#1A3A5F',
  gold:    '#C9A227',
  success: '#2E7D32',
  warning: '#E65100',
  error:   '#C62828',
  neutral: '#757575',
  bg:      '#F9F9F9',
};

const BACKEND = 'https://ecolab-backend-723916953839.us-central1.run.app';

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
}

function initiales(nombre, apellido) {
  const n = (nombre || '').trim()[0] || '';
  const a = (apellido || '').trim()[0] || '';
  return (n + a).toUpperCase() || '?';
}

// ── Config por estado ────────────────────────────────────────────────────────
const estadoConfig = {
  ACTIVO: {
    color:   C.success,
    bgColor: '#E8F5E9',
    Icon:    CheckCircleIcon,
    label:   'CARNET VÁLIDO',
    msg:     null,
    atenuado: false,
  },
  REVOCADO: {
    color:   C.error,
    bgColor: '#FFEBEE',
    Icon:    CancelIcon,
    label:   'CARNET REVOCADO',
    msg:     'Este carnet fue revocado.',
    atenuado: true,
  },
  VENCIDO: {
    color:   C.warning,
    bgColor: '#FFF3E0',
    Icon:    WarningAmberIcon,
    label:   'CARNET VENCIDO',
    msg:     null, // se construye con la fecha
    atenuado: true,
  },
  NO_ENCONTRADO: {
    color:   C.neutral,
    bgColor: '#F5F5F5',
    Icon:    HelpOutlineIcon,
    label:   'CÓDIGO NO RECONOCIDO',
    msg:     'El código escaneado no corresponde a ningún carnet registrado.',
    atenuado: false,
  },
};

// ── Skeleton del carnet (estado de carga) ────────────────────────────────────
function CarnetSkeleton() {
  return (
    <Box sx={cardStyle}>
      <Skeleton variant="rectangular" width="60%" height={52} sx={{ borderRadius: 2, mb: 3, mx: 'auto' }} />
      <Skeleton variant="circular" width={160} height={160} sx={{ mx: 'auto', mb: 2 }} />
      <Skeleton variant="text" width="70%" height={32} sx={{ mx: 'auto', mb: 1 }} />
      <Skeleton variant="text" width="40%" height={22} sx={{ mx: 'auto', mb: 3 }} />
      <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, mb: 2 }} />
      <Skeleton variant="text" width="55%" height={18} sx={{ mx: 'auto', mb: 0.5 }} />
      <Skeleton variant="text" width="55%" height={18} sx={{ mx: 'auto' }} />
    </Box>
  );
}

// ── Estilos reutilizables ────────────────────────────────────────────────────
const cardStyle = {
  width: '100%',
  maxWidth: 480,
  mx: 'auto',
  background: '#fff',
  borderRadius: 4,
  boxShadow: '0 4px 32px rgba(26,58,95,0.10)',
  overflow: 'hidden',
  p: 0,
};

// ── Foto del titular ─────────────────────────────────────────────────────────
function FotoTitular({ fotoUrl, nombre, apellido, atenuado }) {
  const [fotoError, setFotoError] = useState(false);
  const ini = initiales(nombre, apellido);

  const containerStyle = {
    width: 160,
    height: 160,
    borderRadius: '50%',
    mx: 'auto',
    mb: 2,
    border: `4px solid ${C.gold}`,
    overflow: 'hidden',
    position: 'relative',
    opacity: atenuado ? 0.55 : 1,
    boxShadow: `0 4px 20px rgba(201,162,39,0.25)`,
    background: C.navy,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  if (fotoUrl && !fotoError) {
    return (
      <Box sx={containerStyle}>
        <Box
          component="img"
          src={fotoUrl}
          alt={`${nombre} ${apellido}`}
          onError={() => setFotoError(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={containerStyle}>
      <Typography sx={{ color: C.gold, fontSize: '3rem', fontWeight: 800, lineHeight: 1 }}>
        {ini}
      </Typography>
    </Box>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function ValidacionCarnet() {
  const { codigo } = useParams();
  const [estado, setEstado] = useState('cargando'); // cargando | ok | error_red
  const [data,   setData]   = useState(null);

  useEffect(() => {
    if (!codigo) { setEstado('ok'); setData({ estado: 'NO_ENCONTRADO' }); return; }

    // Sanitizar: si el código parece un path traversal tratarlo como inválido
    const codigoLimpio = codigo.replace(/[^a-zA-Z0-9_-]/g, '');
    if (!codigoLimpio) { setEstado('ok'); setData({ estado: 'NO_ENCONTRADO' }); return; }

    const url = `${BACKEND}/api/ecolab/carnet/validar/${encodeURIComponent(codigoLimpio)}`;

    fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { 'Accept': 'application/json' },
    })
      .then(async (res) => {
        const json = await res.json();
        if (res.status === 404 || json.estado === 'NO_ENCONTRADO') {
          setData({ estado: 'NO_ENCONTRADO' });
        } else {
          setData(json);
        }
        setEstado('ok');
      })
      .catch(() => {
        setEstado('error_red');
      });
  }, [codigo]);

  // ── Pantalla de red caída ──────────────────────────────────────────────────
  if (estado === 'error_red') {
    return (
      <PageWrapper>
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <HelpOutlineIcon sx={{ fontSize: 56, color: C.neutral, mb: 2 }} />
          <Typography sx={{ fontWeight: 700, color: C.navy, mb: 1 }}>
            No se pudo verificar el carnet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Verifique su conexión a internet e intente nuevamente.
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  // ── Skeleton mientras carga ───────────────────────────────────────────────
  if (estado === 'cargando') {
    return (
      <PageWrapper>
        <CarnetSkeleton />
      </PageWrapper>
    );
  }

  // ── Resultado ─────────────────────────────────────────────────────────────
  const cfg       = estadoConfig[data?.estado] || estadoConfig.NO_ENCONTRADO;
  const { Icon }  = cfg;
  const atenuado  = cfg.atenuado;
  const titular   = data?.titular || {};
  const coop      = data?.cooperativa || {};
  const noEncontrado = data?.estado === 'NO_ENCONTRADO';

  const mensajeVencido = data?.estado === 'VENCIDO'
    ? `Este carnet venció el ${formatFecha(data.fechaVigencia)}.`
    : null;

  return (
    <PageWrapper>
      <Box sx={cardStyle}>

        {/* ── Header institucional ── */}
        <Box sx={{
          background: C.navy,
          px: 3, pt: 3, pb: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box
            component="img"
            src={logo}
            alt="Capellanías"
            sx={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }}
          />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.2 }}>
              Capellanías Castrenses
            </Typography>
            <Typography sx={{ color: C.gold, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em' }}>
              ALIANZA SOLIDARIA
            </Typography>
          </Box>
          {/* Badge dorado */}
          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
            <Box sx={{
              background: `linear-gradient(135deg, ${C.gold}, #a07c1a)`,
              borderRadius: '50%', width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 2px 10px rgba(201,162,39,0.40)`,
            }}>
              <VerifiedIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
          </Box>
        </Box>

        {/* ── Banner de estado ── */}
        <Box sx={{
          background: cfg.color,
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Icon sx={{ color: '#fff', fontSize: 32, flexShrink: 0 }} />
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem', letterSpacing: '0.04em' }}>
            {cfg.label}
          </Typography>
        </Box>

        {/* ── Mensaje de estado (REVOCADO / VENCIDO / NO_ENCONTRADO) ── */}
        {(cfg.msg || mensajeVencido) && (
          <Box sx={{ background: cfg.bgColor, px: 3, py: 1.5 }}>
            <Typography variant="body2" sx={{ color: cfg.color, fontWeight: 600 }}>
              {mensajeVencido || cfg.msg}
            </Typography>
          </Box>
        )}

        {/* ── Cuerpo del carnet ── */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>

          {!noEncontrado && (
            <>
              {/* Foto */}
              <FotoTitular
                fotoUrl={titular.fotoUrl}
                nombre={titular.nombre}
                apellido={titular.apellido}
                atenuado={atenuado}
              />

              {/* Nombre del titular */}
              <Typography sx={{
                textAlign: 'center', fontWeight: 800,
                fontSize: '1.2rem', color: atenuado ? '#999' : '#1A1A1A',
                mb: 0.5,
              }}>
                {titular.nombre} {titular.apellido}
              </Typography>

              {/* Tipo y número de documento */}
              <Typography sx={{
                textAlign: 'center', fontFamily: 'monospace',
                fontSize: '0.88rem', color: atenuado ? '#bbb' : '#555',
                mb: 3, letterSpacing: '0.08em',
              }}>
                {titular.tipoDocumento} &nbsp; {titular.documento}
              </Typography>

              {/* Panel Número de Registro (exclusivo Capellanías) */}
              {data?.numeroRegistro && (
                <Box sx={{
                  border: `2px solid ${C.gold}`,
                  borderRadius: 2, px: 2.5, py: 1.5, mb: 2.5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'rgba(201,162,39,0.06)',
                  opacity: atenuado ? 0.6 : 1,
                }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.gold, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    N° Registro
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: '#1A1A1A', fontSize: '0.92rem' }}>
                    {data.numeroRegistro}
                  </Typography>
                </Box>
              )}

              {/* Tabla de fechas */}
              <Box sx={{ opacity: atenuado ? 0.6 : 1 }}>
                {[
                  ['Organización',  coop.nombre || coop.siglas || '—'],
                  ['Expedición',    formatFecha(data?.fechaExpedicion)],
                  ['Vigencia',      formatFecha(data?.fechaVigencia)],
                ].map(([k, v]) => (
                  <Box key={k} sx={{
                    display: 'flex', justifyContent: 'space-between',
                    py: 0.8, borderBottom: '1px solid rgba(0,0,0,0.06)',
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{k}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#333', textAlign: 'right', maxWidth: '60%' }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Código NO ENCONTRADO — sin datos */}
          {noEncontrado && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <HelpOutlineIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto' }}>
                El código <strong style={{ fontFamily: 'monospace' }}>{codigo}</strong> no corresponde
                a ningún carnet en nuestra base de datos.
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Footer institucional ── */}
        <Box sx={{
          borderTop: `1px solid rgba(0,0,0,0.07)`,
          px: 3, py: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedIcon sx={{ fontSize: 14, color: C.gold }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.70rem' }}>
              Documento oficial · Verifique escaneando el código QR
            </Typography>
          </Box>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Typography variant="caption" sx={{ color: C.navy, fontSize: '0.68rem', opacity: 0.7,
              '&:hover': { opacity: 1 } }}>
              solidarityallianceofchaplains.org
            </Typography>
          </Link>
        </Box>

      </Box>
    </PageWrapper>
  );
}

// ── Wrapper de página ────────────────────────────────────────────────────────
function PageWrapper({ children }) {
  return (
    <Box sx={{
      minHeight: '100vh',
      background: `linear-gradient(160deg, ${C.navy}18 0%, ${C.bg} 40%)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      px: 2,
      pt: { xs: 3, md: 6 },
      pb: 6,
    }}>
      {children}
    </Box>
  );
}
